<template>
  <div
    class="atb-flex atb-flex-col atb-items-center atb-justify-center atb-bg-gray-100 atb-min-h-full atb-py-8"
  >
    <form
      class="atb-w-full atb-max-w-2xl atb-flex atb-flex-col atb-items-center atb-justify-center atb-gap-6 atb-px-8 atb-py-6"
      @submit.prevent="handleSubmit"
    >
      <div class="atb-text-center">
        <h2 class="atb-text-2xl atb-font-bold atb-text-gray-800">
          {{ t('deployToEngine') }}
        </h2>
        <p class="atb-text-sm atb-text-gray-500 atb-mt-1">
          {{ t('selectDeploymentFile') }}
        </p>
      </div>

      <input
        ref="fileInputRef"
        type="file"
        accept=".zip,application/zip,application/x-zip-compressed,application/x-zip"
        class="atb-hidden"
        @change="handleFileChange"
      />

      <div
        class="atb-w-full atb-flex atb-flex-col atb-items-center atb-justify-center atb-gap-3 atb-border-2 atb-border-dashed atb-rounded-xl atb-py-10 atb-px-6 atb-cursor-pointer atb-transition-all atb-duration-200"
        :class="
          isDragging
            ? 'atb-border-blue-500 atb-bg-blue-50'
            : selectedFile
            ? 'atb-border-green-400 atb-bg-green-50'
            : 'atb-border-gray-300 atb-bg-gray-50 hover:atb-border-blue-400 hover:atb-bg-blue-50'
        "
        @click="fileInputRef?.click()"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
      >
        <div
          class="atb-flex atb-items-center atb-justify-center atb-w-14 atb-h-14 atb-rounded-full"
          :class="selectedFile ? 'atb-bg-green-100' : 'atb-bg-blue-100'"
        >
          <span
            class="material-icons atb-text-4xl"
            :class="selectedFile ? 'atb-text-green-500' : 'atb-text-blue-500'"
          >
            {{ selectedFile ? 'check_circle' : 'cloud_upload' }}
          </span>
        </div>

        <div class="atb-text-center">
          <p
            v-if="selectedFile"
            class="atb-text-sm atb-font-semibold atb-text-green-700"
          >
            {{ selectedFileName }}
          </p>
          <p
            v-else
            class="atb-text-sm atb-font-medium atb-text-gray-700"
          >
            Drag & drop a file here, or
            <span class="atb-text-blue-600 atb-underline atb-underline-offset-2"
              >browse</span
            >
          </p>
          <p
            v-if="!selectedFile"
            class="atb-text-xs atb-text-gray-400 atb-mt-1"
          >
            Only ZIP file accepted
          </p>
          <p
            v-if="selectedFile"
            class="atb-text-xs atb-text-gray-400 atb-mt-1"
          >
            Click to change file
          </p>
        </div>
      </div>

      <p
        v-if="fileError"
        class="atb-text-xs atb-text-red-500 atb-self-start"
      >
        {{ fileError }}
      </p>

      <div class="atb-flex atb-gap-3 atb-w-full atb-justify-center">
        <Button
          v-if="selectedFile"
          motif="basic"
          type="button"
          @click.stop="clearFile"
        >
          Clear
        </Button>
        <Button
          motif="primary"
          type="submit"
          :disabled="!selectedFile"
        >
          Deploy
        </Button>
      </div>
    </form>
  </div>
  <StreamingProgressModal
    :show="showProgressModal"
    :header-text="$t('uploadingDeployment')"
    :message="progressMessage"
    :default-message="$t('deployProgressMessage')"
    :percent="progressPercent"
    :is-closable="false"
    @on-modal-close="showProgressModal = false"
  />
</template>
<script lang="ts" setup>
  import { ref } from 'vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import { useI18n } from 'vue-i18n';
  import { useDeployStore } from './deploy-store';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { DeployProgressEvent } from '@/app/shared/types/models/deploy.interface';
  import StreamingProgressModal from '@/app/shared/components/StreamingProgressModal.vue';

  const fileInputRef = ref<HTMLInputElement | null>(null);
  const selectedFile = ref<File | null>(null);
  const selectedFileName = ref('');
  const fileError = ref('');
  const isDragging = ref(false);
  const { t } = useI18n();
  const showProgressModal = ref(false);
  const progressMessage = ref('');
  const progressPercent = ref(0);
  const isSubmitting = ref(false);
  const alertStore = useAlertStore();
  const deployStore = useDeployStore();

  function setFile(file: File) {
    selectedFile.value = file;
    selectedFileName.value = file.name;
    fileError.value = '';
  }

  function setInvalidFile() {
    selectedFile.value = null;
    selectedFileName.value = '';
    fileError.value = t('invalidFileType');
    if (fileInputRef.value) {
      fileInputRef.value.value = '';
    }
  }

  function handleFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (validateFile(file)) {
        setFile(file);
      } else {
        setInvalidFile();
      }
    }
  }

  function handleDrop(event: DragEvent) {
    isDragging.value = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    if (validateFile(file)) {
      setFile(file);
    } else {
      setInvalidFile();
    }
  }

  function clearFile() {
    selectedFile.value = null;
    selectedFileName.value = '';
    fileError.value = '';
    if (fileInputRef.value) {
      fileInputRef.value.value = '';
    }
  }

  const validateFile = (file: File): boolean => {
    fileError.value = '';

    if (!file.name.toLowerCase().endsWith('.zip')) {
      fileError.value = t('invalidFileType');
      return false;
    }

    const validMimeTypes = [
      'application/zip',
      'application/x-zip-compressed',
      'application/x-zip',
    ];
    if (file.type && !validMimeTypes.includes(file.type)) {
      fileError.value = t('invalidFileType');
      return false;
    }

    return true;
  };

  async function handleSubmit() {
    if (!selectedFile.value) {
      fileError.value = 'Please select a file before deploying.';
      return;
    }
    if (!validateFile(selectedFile.value)) {
      return;
    }
    isSubmitting.value = true;
    showProgressModal.value = true;
    progressMessage.value = '';
    progressPercent.value = 0;
    try {
      const response = await deployStore.upload(
        selectedFile.value,
        (event: DeployProgressEvent) => {
          progressMessage.value = event.message;
          if (event.total > 0) {
            progressPercent.value = Math.round(
              (event.current / event.total) * 100
            );
          }
        }
      );
      progressPercent.value = 100;

      alertStore.showNotification(
        t('deploySuccessful', response.deploymentFileName),
        t('engine'),
        Motif.SUCCESS
      );
      clearFile();
      isSubmitting.value = false;
    } catch (error: any) {
      console.error('Deployment failed:', error);
      alertStore.showNotification(
        error?.message || error?.response?.data?.message || t('deployFailed'),
        t('engine'),
        Motif.ERROR
      );
    } finally {
      isSubmitting.value = false;
      showProgressModal.value = false;
      progressMessage.value = '';
      progressPercent.value = 0;
    }
  }
</script>
